import { useState, createContext, useEffect, useContext } from 'react';
import { useUserContext } from '../user_context/UserProvider';
import { SignalRContext } from '../signal_r_context/SignalRListener';
import { getAllLiveByObject } from '../../repository/AppVersionDAO';
import { invokeAppVersion } from '../../repository/SignalRDAO';
import packageJson from '../../../package.json';
export const AppVersionContext = createContext();

export const AppVersionProvider = ({ children }) => {
  const connection = useContext(SignalRContext);
  const { bearerToken, safeguardView, isSafeguardDev } = useUserContext();
  const [versionData, setVersionData] = useState();
  const currentVersion = parseFloat(packageJson.version);
  const [relevantVersion, setRelevantVersion] = useState();
  const [inMaintenance, setInMaintenance] = useState(false);
  const [futureUpdateRequired, setFutureUpdateRequired] = useState(false);
  const [rolloutTimestamp, setRolloutTimestamp] = useState(false);
  const [updateAcknowledged, setUpdateAcknowledged] = useState(false);

  useEffect(() => {
    if (bearerToken) {
      const fetchVersionData = async () => {
        try {
          const result = await getAllLiveByObject('version_web_app');
          setVersionData(result);
          invokeVersion();
        } catch (e) {
          console.error(e);
        }
      };
      fetchVersionData();
    }
  }, [bearerToken, safeguardView]);

  useEffect(() => {
    if (versionData) {
      const filteredVersion = filterOutIrrelevantVersions(versionData);
      if (filteredVersion) setRelevantVersion(filteredVersion);
    }
  }, [versionData]);

  useEffect(() => {
    if (relevantVersion) {
      if (
        relevantVersion.required &&
        !relevantVersion.update_complete &&
        relevantVersion.go_live
      ) {
        setFutureUpdateRequired(true);
      } else {
        setFutureUpdateRequired(false);
      }
      const currentTime = Date.now();
      if (
        currentTime > relevantVersion.rollout_timestamp &&
        !relevantVersion.update_complete &&
        relevantVersion.rollout_timestamp !== null &&
        !isSafeguardDev
      ) {
        setInMaintenance(true);
      } else {
        setInMaintenance(false);
      }
      setRolloutTimestamp(relevantVersion.rollout_timestamp);
    }
  }, [relevantVersion]);

  const filterOutIrrelevantVersions = (versions) => {
    const filteredVersions = versions
      .filter(
        (version) =>
          (version.version > currentVersion && !version.update_complete) ||
          (version.version === currentVersion && !version.update_complete),
      )
      .filter((version) => version.go_live);
    if (filteredVersions && filteredVersions.length === 0) {
      return;
    }
    const greatestVersion = filteredVersions.reduce((max, version) =>
      version.version > max.version ? version : max,
    );

    return greatestVersion;
  };

  const invokeVersion = async () => {
    if (
      connection &&
      connection.connection &&
      connection.connection.connectionId
    ) {
      await invokeAppVersion(
        true,
        connection.connection.connectionId,
        bearerToken,
      );
    }
  };

  const updateWebVersion = (newAppData) => {
    if (versionData) {
      const updatedVersionData = versionData.map((version) => {
        if (version.id === newAppData.id) {
          return newAppData;
        }
        return version;
      });
      setVersionData(updatedVersionData);
      const filteredVersion = filterOutIrrelevantVersions(updatedVersionData);
      setRelevantVersion(filteredVersion);

      if (filteredVersion && filteredVersion.length > 0) {
        const version = filteredVersion[0];
        if (version.required && !version.update_complete)
          setFutureUpdateRequired(true);
        const currentTime = Date.now();
        if (currentTime > version.rollout_timestamp && !version.update_complete)
          setInMaintenance(true);
        else setInMaintenance(false);
        setRolloutTimestamp(version.rollout_timestamp);
      }
    } else {
      console.warn('Version data is not available.');
    }
  };

  return (
    <AppVersionContext.Provider
      value={{
        versionData,
        inMaintenance,
        futureUpdateRequired,
        updateAcknowledged,
        setUpdateAcknowledged,
        rolloutTimestamp,
        updateWebVersion,
        setInMaintenance,
      }}
    >
      {children}
    </AppVersionContext.Provider>
  );
};

export const useAppVersionProvider = () => {
  return useContext(AppVersionContext);
};
